import { apiClient } from "../client";
import type { Organization } from "./authService";

export interface GetOrganizationsResponse {
  organizations: Organization[];
}

export interface CreateOrganizationData {
  name: string;
  slug: string;
  logo?: string;
  url?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  postalCode?: string;
}

export interface CreateOrganizationResponse {
  message: string;
  organization: Organization;
}

export interface UpdateOrganizationData {
  name?: string;
  logo?: string;
  url?: string;
  currency?: string;
  currencySymbol?: string;
  timezone?: string;
  timezoneOffset?: string;
  dateFormat?: string;
  financialYearStart?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  postalCode?: string;
}

export interface UpdateOrganizationResponse {
  message: string;
  organization: Organization;
}

export interface CheckSlugResponse {
  available: boolean;
  slug: string;
  message: string;
  suggestions?: string[];
}

export interface RemoveMemberResponse {
  message: string;
}

export interface ResendInvitationResponse {
  message: string;
  invitationToken: string;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
  code?: string;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export class OrganizationService {
  /**
   * Get user's organizations
   */
  static async getMyOrganizations(): Promise<GetOrganizationsResponse> {
    return await apiClient.get<GetOrganizationsResponse>(
      "/organizations/my-organizations",
    );
  }

  /**
   * Get organization by ID
   */
  static async getOrganizationById(
    organizationId: number,
  ): Promise<Organization> {
    return await apiClient.get<Organization>(
      `/organizations/${organizationId}`,
    );
  }

  /**
   * Get organization by slug
   */
  static async getOrganizationBySlug(slug: string): Promise<Organization> {
    return await apiClient.get<Organization>(`/organizations/slug/${slug}`);
  }

  /**
   * Create a new organization
   */
  static async createOrganization(
    orgData: CreateOrganizationData,
  ): Promise<CreateOrganizationResponse> {
    return await apiClient.post<CreateOrganizationResponse>(
      "/organizations",
      orgData,
    );
  }

  /**
   * Update an existing organization
   */
  static async updateOrganization(
    organizationId: number,
    orgData: UpdateOrganizationData,
  ): Promise<UpdateOrganizationResponse> {
    return await apiClient.put<UpdateOrganizationResponse>(
      `/organizations/${organizationId}`,
      orgData,
    );
  }

  /**
   * Check slug availability
   */
  static async checkSlugAvailability(slug: string): Promise<CheckSlugResponse> {
    return await apiClient.get<CheckSlugResponse>(
      `/organizations/check-slug?slug=${slug}`,
    );
  }

  /**
   * Get organization members
   */
  static async getOrganizationMembers(organizationId: number) {
    return await apiClient.get(`/organizations/${organizationId}/members`);
  }

  /**
   * Remove member from organization
   */
  static async removeOrganizationMember(
    organizationId: number,
    memberId: number,
  ): Promise<RemoveMemberResponse> {
    try {
      return await apiClient.delete<RemoveMemberResponse>(
        `/organizations/${organizationId}/members/${memberId}`,
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      throw {
        code: axiosError.response?.data?.code || "UNKNOWN_ERROR",
        message:
          axiosError.response?.data?.message || "Failed to remove member",
      };
    }
  }

  /**
   * Resend invitation to pending member
   */
  static async resendMemberInvitation(
    organizationId: number,
    memberId: number,
  ): Promise<ResendInvitationResponse> {
    try {
      return await apiClient.post<ResendInvitationResponse>(
        `/organizations/${organizationId}/members/${memberId}/resend-invite`,
        {},
      );
    } catch (error: unknown) {
      const axiosError = error as AxiosErrorResponse;
      throw {
        code: axiosError.response?.data?.code || "UNKNOWN_ERROR",
        message:
          axiosError.response?.data?.message || "Failed to resend invitation",
      };
    }
  }
}
